import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  TranslationService
} from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import {
  DeliveryNotesOverview,
  DeliveryNote,
  DeliveryNoteItem,
  DeliveryNotesOverviewRequest ,
  DeliveryNotesOverviewDefaultSortOptions
} from '../../delivery-notes.models';
import { DeliveryNotesService } from '../../delivery-notes.service';
import { getDeliveryNotesOverview, clearDeliveryNotesOverview, getMarketplaceDeliveryNotesOverview } from '../../store/delivery-notes.actions';
import { selectOverview, errorOverview, loadingOverview } from '../../store/delivery-notes.selectors';

@Component({
    selector: 'app-marketplace-delivery-notes-overview',
    templateUrl: './marketplace-delivery-notes-overview.component.html',
    styleUrls: [
        './marketplace-delivery-notes-overview.component.scss',
        './marketplace-delivery-notes-overview-print.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MarketplaceDeliveryNotesOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('searchForm') searchForm: NgForm;


  showDefault: boolean = true;
  deliveryId: string = '';
  productCode: string = '';
  todaysDate: Date;
  deliveryDateFrom: string;
  deliveryDateFromMin: string;
  deliveryDateFromMax: Date = new Date();
  deliveryDateTo: string;
  deliveryDateToMin: string;
  deliveryDateToMax: Date = new Date();

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'desc';
  compare = (v1: string | number | Date, v2: string | number | Date) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  overview$: Observable<DeliveryNotesOverview> = this.store.select(
    selectOverview
  );
  overview: DeliveryNotesOverview;
  deliveryNotes: DeliveryNote[];
  deliveryNoteItems: DeliveryNoteItem[];
  isLoadingOverview$: Observable<boolean> = this.store.select(loadingOverview);
  errorOverview$: Observable<Error> = this.store.select(errorOverview);
  errorOverview: Error;


  constructor(
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private modalService: NgbModal,
    private deliveryNotesService: DeliveryNotesService,
    private store: Store<{ overview: DeliveryNotesOverview }>
  ) {}


setLocalSearch(search: DeliveryNotesOverviewRequest) {
  this.deliveryId = search.deliveryId;
  this.productCode = search.productCode;
  this.deliveryDateFrom = search.deliveryDateFrom;
  this.deliveryDateTo = search.deliveryDateTo;
}

  ngOnInit() {
    this.setSortOptions();
    this.setLocalSearch(this.deliveryNotesService.getSearchData());
    this.fetchOrders();
    this.displayOrders();
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearDeliveryNotesOverview());
  }

  setSortOptions() {
    let savedSearchData = this.deliveryNotesService.getSortData();
    if(savedSearchData && savedSearchData.sortOption)
    {
      this.selectedSortOption = savedSearchData.sortOption;
      this.sortDirection = savedSearchData.sortDirection;
    }
    else{
      this.selectedSortOption =
      DeliveryNotesOverviewDefaultSortOptions[
        DeliveryNotesOverviewDefaultSortOptions.deliveryNumber
        ];
    }
    this.sortOptions = Object.values(
      DeliveryNotesOverviewDefaultSortOptions
    ).filter((value) => typeof value === 'string') as string[];
  }

  onChangeSort() {
    this.deliveryNotesService.saveSortData(
      this.selectedSortOption,
      this.sortDirection
    );
    this.deliveryNotes = [...this.overview.entries].sort((a, b) => {
      const res = this.compare(
        a[this.selectedSortOption],
        b[this.selectedSortOption]
      );
      return this.sortDirection === 'asc' ? res : -res;
    });
  }

  onChangeFilter() {
  }

  onReset () {
    this.deliveryNotesService.clearSortData();
    this.deliveryNotesService.clearSearchData();
    this.setLocalSearch(this.deliveryNotesService.getSearchData());
    this.searchForm.reset({
      deliveryId: '',
      productCode: '',
      deliveryDateFrom: this.deliveryNotesService.getSearchData().deliveryDateFrom,
      deliveryDateTo: this.deliveryNotesService.getSearchData().deliveryDateTo
    });
    this.selectedSortOption = this.deliveryNotesService.getSortData().sortOption;
    this.sortDirection = this.deliveryNotesService.getSortData().sortDirection;
    this.fetchOrders();
    this.displayOrders();
    //refresh the control in the search form
    let controls = this.searchForm.controls;
    controls["deliveryDateFrom"].setValue(this.deliveryDateFrom);
    controls["deliveryDateFrom"].parent.markAllAsTouched();
  }

  onSubmit() {
    this.deliveryId = this.searchForm.value.deliveryId;
    this.productCode = this.searchForm.value.productCode;
    this.deliveryDateFrom = this.searchForm.value.deliveryDateFrom;
    this.deliveryDateTo = this.searchForm.value.deliveryDateTo;
    this.deliveryNotesService.saveSearchData(
      this.deliveryId,
      this.productCode,
      this.deliveryDateFrom,
      this.deliveryDateTo
    );
    this.fetchOrders();
    this.displayOrders();
  }

  onCancelOrder(event: any, confirmationMessage) {

  }

  onExport() {
    this.deliveryId = this.searchForm.value.deliveryId;
    this.productCode = this.searchForm.value.productCode;
    this.deliveryDateFrom = this.searchForm.value.deliveryDateFrom;
    this.deliveryDateTo = this.searchForm.value.deliveryDateTo;
    this.deliveryNotesService.saveSearchData(
      this.deliveryId,
      this.productCode,
      this.deliveryDateFrom,
      this.deliveryDateTo
    );
   this.deliveryNotesService.printOrExportDeliveryNotesOverview(this.getRequestData(), true);
  }

  getRequestData(): DeliveryNotesOverviewRequest {
    var requestData = {} as DeliveryNotesOverviewRequest;
    requestData.deliveryId = this.deliveryId;
    requestData.productCode = this.productCode;
    requestData.deliveryDateTo = this.deliveryDateTo;
    requestData.deliveryDateFrom = this.deliveryDateFrom;
    return requestData;
  }

  fetchOrders(): void {
    this.overview = null;
    this.deliveryNotes = null;
    this.deliveryNoteItems = null;
    this.errorOverview = null;
    this.store.dispatch(getMarketplaceDeliveryNotesOverview({
      formData: this.getRequestData()
    }));
  }

  displayOrders(): void {
    this.overview$
      .pipe(
        filter((overview) => overview != null),
        tap((overview) => {
          this.overview = overview;
          this.deliveryNotes = [...overview.entries];
          this.onChangeSort();
        })
      )
      .subscribe();
    this.errorOverview$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorOverview = error;
        }),
        switchMap(() => this.translation.translate('deliveryNotes.error')),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
  }

  changeDeliveryDateFrom(event: any) {
    this.deliveryDateFrom = event.target.value;
    this.deliveryDateToMin = event.target.value;
    if (new Date(this.deliveryDateFrom) > new Date(this.deliveryDateTo)) {
      this.deliveryDateTo = this.deliveryDateFrom;
    }
  }

  changeDeliveryDateTo(event: any) {
    this.deliveryDateTo = event.target.value;
  }
}
